import { useEffect, useRef } from "react"

import { useTranslation } from "react-i18next"

import DescriptorRow from "../DescriptorRow/DescriptorRow"
import DescriptorRowSkeleton from "../DescriptorRow/DescriptorRowSkeleton"

import styles from "./descriptor-list.module.less"

export const DescriptorList = ({
  descriptors,
  onRowLogsClick,
  hasMore,
  onLoadMore,
  usersViewForAdmin,
  onStatusChange,
  onMatchTypeChange
}) => {
  const { t } = useTranslation()
  const listRef = useRef(null)
  const sentinelRef = useRef(null)
  const onLoadMoreRef = useRef(onLoadMore)
  onLoadMoreRef.current = onLoadMore

  // On desktop `.list` is a scroll container (bounded max-height + overflow-y).
  // On mobile the media query drops max-height, so the whole page scrolls and
  // the list itself never clips — passing it as `root` would make the sentinel
  // permanently intersect and fire on every observe(). Detect the actual scroll
  // container and fall back to the viewport (`root: null`) when the list is not
  // one. Deps intentionally exclude `rows.length` — the sentinel DOM node is
  // stable across page appends, so recreating the observer on every fetched
  // page would re-emit its initial "currently intersecting" callback and load
  // every remaining page in one tick.
  useEffect(() => {
    if (!hasMore) return
    const listEl = listRef.current
    const target = sentinelRef.current
    if (!listEl || !target) return
    const isScrollable = listEl.scrollHeight > listEl.clientHeight + 1
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMoreRef.current?.()
      },
      { root: isScrollable ? listEl : null, rootMargin: "200px" }
    )
    io.observe(target)
    return () => io.disconnect()
  }, [hasMore])

  if (descriptors?.fetching && !descriptors?.rows?.length) {
    return (
      <div className={styles.list}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i}>
            <DescriptorRowSkeleton />
            {i < 3 ? <div className={styles.rowDivider} /> : null}
          </div>
        ))}
      </div>
    )
  }
  if (descriptors?.error) return <div className={styles.listEmpty}>{descriptors.error}</div>
  if (!descriptors?.rows?.length) {
    return <div className={styles.listEmpty}>{t("No descriptors found")}</div>
  }
  return (
    <div ref={listRef} className={styles.list}>
      {descriptors.rows.map((row, i) => (
        <div key={row.record_id} data-testid={`deflect-business-descriptor-row-${row.record_id}`}>
          <DescriptorRow
            row={row}
            onLogsClick={() => onRowLogsClick?.(row)}
            usersViewForAdmin={usersViewForAdmin}
            onStatusChange={(status) => onStatusChange?.(row, status)}
            onMatchTypeChange={(matchType) => onMatchTypeChange?.(row, matchType)}
          />
          {i < descriptors.rows.length - 1 ? <div className={styles.rowDivider} /> : null}
        </div>
      ))}
      {hasMore ? (
        <div
          ref={sentinelRef}
          className={styles.sentinel}
          data-testid="deflect-business-descriptor-sentinel"
        >
          {descriptors.fetching ? (
            <>
              <div className={styles.rowDivider} />
              <DescriptorRowSkeleton />
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
